/**
 * Full-screen launch sheet for WebXR AR. Plain DOM (not React) so every AR
 * entry point — the canvas button, the controller's `requestAR` flag, or a
 * direct `engine.activateAR()` call — gets the same experience. The sheet
 * hosts the `<model-viewer>` preview and owns the "Place in my space" call to
 * action; `model-viewer-ar.cfn.ts` drives it.
 */

/** Handle to the open launch sheet, held by ExportManager so stopAR()/dispose() can dismiss it. */
export type ARLaunchSheetHandle = {
	close: () => void;
	isOpen: () => boolean;
};

export type ARLaunchSheet = {
	handle: ARLaunchSheetHandle;
	mountViewer: (viewer: HTMLElement) => void;
	showPreparing: (message: string) => void;
	showReady: (canActivateAR: boolean) => void;
	showHint: (message: string) => void;
	showError: (message: string) => void;
	setBusy: (busy: boolean) => void;
	onLaunch: (handler: () => void) => void;
};

export const AR_SHEET_MESSAGES = {
	preparing: 'Preparing your kitchen…',
	loadingPreview: 'Loading the AR preview…',
	ready: 'Point your camera at the floor, then tap to place your kitchen at real size.',
	starting: 'Starting AR…',
	unavailable: 'AR is not available in this browser. On Android, open this page in Chrome with Google Play Services for AR installed.',
	failed: 'AR could not start. Allow camera access and make sure Google Play Services for AR is installed, then reload this page and try again.'
};

const LAUNCH_LABEL = 'Place in my space';
const STYLE_ATTRIBUTE = 'data-rta-ar-launch-sheet';
const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const SHEET_STYLES = `
.rta-ar-sheet {
	position: fixed; inset: 0; z-index: 10000; display: flex; flex-direction: column;
	background: #f6f6f7; color: #17181c; font-family: ${FONT_STACK};
	touch-action: none; overscroll-behavior: contain; -webkit-tap-highlight-color: transparent;
}
.rta-ar-sheet__header {
	display: flex; align-items: center; justify-content: space-between; gap: 12px;
	padding: calc(12px + env(safe-area-inset-top)) 16px 8px;
}
.rta-ar-sheet__title { margin: 0; font-size: 17px; font-weight: 600; }
.rta-ar-sheet__close {
	width: 40px; height: 40px; padding: 0; border-radius: 50%; border: 1px solid rgba(0, 0, 0, 0.08);
	background: rgba(255, 255, 255, 0.9); color: #17181c; font-size: 24px; line-height: 1; cursor: pointer;
	display: flex; align-items: center; justify-content: center;
}
.rta-ar-sheet__stage { position: relative; flex: 1; min-height: 0; }
.rta-ar-sheet__stage model-viewer {
	display: block; width: 100%; height: 100%; background: transparent;
	--poster-color: transparent; --progress-bar-color: transparent;
}
.rta-ar-sheet__status {
	position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
	gap: 14px; padding: 24px; text-align: center; color: #4b5563; font-size: 15px; pointer-events: none;
}
.rta-ar-sheet__spinner {
	width: 28px; height: 28px; border-radius: 50%; border: 3px solid rgba(0, 0, 0, 0.12); border-top-color: #17181c;
	animation: rta-ar-spin 0.8s linear infinite;
}
@keyframes rta-ar-spin { to { transform: rotate(360deg); } }
.rta-ar-sheet__footer {
	display: flex; flex-direction: column; gap: 10px;
	padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
}
.rta-ar-sheet__primary {
	height: 52px; border: none; border-radius: 14px; background: #17181c; color: #ffffff;
	font-size: 16px; font-weight: 600; cursor: pointer; touch-action: manipulation;
	display: flex; align-items: center; justify-content: center; gap: 10px;
}
.rta-ar-sheet__primary:disabled { opacity: 0.45; cursor: default; }
.rta-ar-sheet__hint { margin: 0; min-height: 18px; font-size: 13px; line-height: 1.4; text-align: center; color: #6b7280; }
.rta-ar-sheet__hint--error { color: #b42318; }
`;

const CUBE_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
	'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
	'<path d="M4 8V6a2 2 0 0 1 2-2h2"/><path d="M16 4h2a2 2 0 0 1 2 2v2"/>' +
	'<path d="M20 16v2a2 2 0 0 1-2 2h-2"/><path d="M8 20H6a2 2 0 0 1-2-2v-2"/>' +
	'<path d="M12 7.6l4.2 2.4v4.8L12 17.2l-4.2-2.4v-4.8L12 7.6z"/>' +
	'<path d="M12 12.4l4.2-2.4"/><path d="M12 12.4v4.8"/><path d="M12 12.4L7.8 10"/></svg>';

const SHEET_MARKUP = `
<div class="rta-ar-sheet__header">
	<h2 class="rta-ar-sheet__title">See it in your space</h2>
	<button type="button" class="rta-ar-sheet__close" aria-label="Close">&times;</button>
</div>
<div class="rta-ar-sheet__stage">
	<div class="rta-ar-sheet__status">
		<div class="rta-ar-sheet__spinner"></div>
		<div class="rta-ar-sheet__status-text"></div>
	</div>
</div>
<div class="rta-ar-sheet__footer">
	<button type="button" class="rta-ar-sheet__primary" disabled>${CUBE_ICON}<span class="rta-ar-sheet__primary-label">${LAUNCH_LABEL}</span></button>
	<p class="rta-ar-sheet__hint"></p>
</div>`;

type SheetParts = {
	stage: HTMLDivElement;
	status: HTMLDivElement;
	statusText: HTMLDivElement;
	primary: HTMLButtonElement;
	primaryLabel: HTMLSpanElement;
	hint: HTMLParagraphElement;
	closeButton: HTMLButtonElement;
};

const ensureSheetStyles = (): void => {
	if (document.head.querySelector(`style[${STYLE_ATTRIBUTE}]`)) return;
	const style = document.createElement('style');
	style.setAttribute(STYLE_ATTRIBUTE, '');
	style.textContent = SHEET_STYLES;
	document.head.appendChild(style);
};

const createSheetRoot = (): HTMLDivElement => {
	const root = document.createElement('div');
	root.className = 'rta-ar-sheet';
	root.setAttribute('role', 'dialog');
	root.setAttribute('aria-modal', 'true');
	root.setAttribute('aria-label', 'See it in your space');
	root.innerHTML = SHEET_MARKUP;
	return root;
};

const querySheetParts = (root: HTMLElement): SheetParts => {
	const query = <T extends HTMLElement>(selector: string): T => root.querySelector(selector) as T;
	return {
		stage: query<HTMLDivElement>('.rta-ar-sheet__stage'),
		status: query<HTMLDivElement>('.rta-ar-sheet__status'),
		statusText: query<HTMLDivElement>('.rta-ar-sheet__status-text'),
		primary: query<HTMLButtonElement>('.rta-ar-sheet__primary'),
		primaryLabel: query<HTMLSpanElement>('.rta-ar-sheet__primary-label'),
		hint: query<HTMLParagraphElement>('.rta-ar-sheet__hint'),
		closeButton: query<HTMLButtonElement>('.rta-ar-sheet__close')
	};
};

type SheetView = Omit<ARLaunchSheet, 'handle' | 'onLaunch'>;

const createSheetView = (parts: SheetParts): SheetView => {
	const { stage, status, statusText, primary, primaryLabel, hint } = parts;

	const showHint = (message: string): void => {
		hint.textContent = message;
		hint.classList.remove('rta-ar-sheet__hint--error');
	};

	const showError = (message: string): void => {
		hint.textContent = message;
		hint.classList.add('rta-ar-sheet__hint--error');
	};

	const showPreparing = (message: string): void => {
		status.style.display = 'flex';
		statusText.textContent = message;
		primary.disabled = true;
		showHint('');
	};

	const showReady = (canActivateAR: boolean): void => {
		status.style.display = 'none';
		if (canActivateAR) {
			primary.style.display = 'flex';
			primary.disabled = false;
			showHint(AR_SHEET_MESSAGES.ready);
			return;
		}
		primary.style.display = 'none';
		showError(AR_SHEET_MESSAGES.unavailable);
	};

	const setBusy = (busy: boolean): void => {
		primary.disabled = busy;
		primaryLabel.textContent = busy ? AR_SHEET_MESSAGES.starting : LAUNCH_LABEL;
	};

	const mountViewer = (viewer: HTMLElement): void => {
		stage.insertBefore(viewer, status);
	};

	return { showHint, showError, showPreparing, showReady, setBusy, mountViewer };
};

/**
 * Build the launch sheet and append it to <body>. `onClose` fires exactly once,
 * whether the user tapped the close button, pressed Escape, or the owner called
 * `handle.close()`.
 */
export const createARLaunchSheet = (onClose: () => void): ARLaunchSheet => {
	ensureSheetStyles();
	const root = createSheetRoot();
	const parts = querySheetParts(root);
	const view = createSheetView(parts);
	const abort = new AbortController();
	let open = true;
	let launchHandler: (() => void) | null = null;

	const close = (): void => {
		if (!open) return;
		open = false;
		abort.abort();
		root.remove();
		onClose();
	};

	parts.closeButton.addEventListener('click', close, { signal: abort.signal });
	parts.primary.addEventListener('click', () => {
		launchHandler?.();
	}, { signal: abort.signal });
	document.addEventListener('keydown', (event: KeyboardEvent) => {
		if (event.key === 'Escape') close();
	}, { signal: abort.signal });

	document.body.appendChild(root);

	return {
		...view,
		handle: { close, isOpen: () => open },
		onLaunch: (handler: () => void): void => {
			launchHandler = handler;
		}
	};
};
